/**
 * App state machine:
 *   signed-out  -> sign-in button
 *   signed-in   -> the routed views (hash routes, so the SWA needs no rewrites):
 *                    #/              request list (the landing view)
 *                    #/new           new onboarding form
 *                    #/new-offboarding  new offboarding form (Phase 2c)
 *                    #/requests/12   read-only request view (+ cancel)
 *                    #/admin[/tab]   admin tabs: templates | access | assignments (Phase 2d; Super Admin or HR)
 *   no-access   -> "You don't have access to MOW Lifecycle" (GET /api/me returned 403)
 * plus a transient "problem" card when the API cannot be reached.
 */
import { html, nothing, render, type TemplateResult } from 'lit-html';
import './styles/mow-tokens.css';
import './styles/app.css';
import { account, init, signIn, signOut } from './auth';
import { ApiError, getMe, type Me } from './api';
import type { App } from './app';
import { header, noAccess, problem, signedOut } from './views';
import { enterList, listView } from './list';
import { enterForm, formView } from './form';
import { enterOffboarding, offboardingView } from './offboarding';
import { detailView, enterDetail } from './detail';
import { adminView, enterAdmin, isAdminTab, type AdminTab } from './admin';

type State =
  | { kind: 'signed-out'; busy: boolean }
  | { kind: 'loading' }
  | { kind: 'signed-in'; me: Me }
  | { kind: 'no-access'; upn: string }
  | { kind: 'problem'; message: string };

type Route = { name: 'list' } | { name: 'new' } | { name: 'new-offboarding' } | { name: 'request'; id: number } | { name: 'admin'; tab: AdminTab };

const root = document.getElementById('app')!;
let state: State = { kind: 'loading' };
let route: Route = { name: 'list' };
/** A notice is raised before a navigation and shown on the view it lands on; the next navigation clears it. */
let notice: { message: string; tone: 'ok' | 'err'; shownOn?: string } | null = null;
let app: App | undefined;

function parseRoute(hash: string): Route {
  const h = hash.replace(/^#/, '');
  if (h === '/new') return { name: 'new' };
  if (h === '/new-offboarding') return { name: 'new-offboarding' };
  const m = /^\/requests\/(\d{1,15})$/.exec(h);
  if (m) return { name: 'request', id: Number(m[1]) };
  const a = /^\/admin(?:\/([a-z]+))?$/.exec(h);
  if (a) return { name: 'admin', tab: isAdminTab(a[1]) ? a[1] : 'templates' };
  return { name: 'list' };
}

function toggleTheme(): void {
  const el = document.documentElement;
  const next = el.getAttribute('data-theme') === 'dark' ? 'light' : 'dark';
  el.setAttribute('data-theme', next);
  try {
    localStorage.setItem('mow.theme', next);
  } catch {
    /* private mode */
  }
}

function set(next: State): void {
  state = next;
  if (state.kind === 'signed-in') {
    const me = state.me;
    app = {
      me,
      go: (hash) => {
        if (location.hash === hash) enterRoute();
        else location.hash = hash; // hashchange -> enterRoute
      },
      redraw: draw,
      notify: (message, tone = 'ok') => {
        notice = { message, tone };
      },
    };
    enterRoute();
    return;
  }
  draw();
}

/** Runs the entered view's loader once per navigation, then draws. */
function enterRoute(): void {
  route = parseRoute(location.hash);
  const key = location.hash || '#/';
  if (notice) {
    if (notice.shownOn === undefined) notice.shownOn = key;
    else if (notice.shownOn !== key) notice = null;
  }
  if (!app) return draw();
  if (route.name === 'list') enterList(app);
  else if (route.name === 'new') enterForm(app);
  else if (route.name === 'new-offboarding') enterOffboarding(app);
  else if (route.name === 'admin') enterAdmin(app, route.tab);
  else enterDetail(app, route.id);
  draw();
  window.scrollTo(0, 0);
}

function routed(a: App): TemplateResult {
  switch (route.name) {
    case 'new':
      return formView(a);
    case 'new-offboarding':
      return offboardingView(a);
    case 'request':
      return detailView(a);
    case 'admin':
      return adminView(a);
    default:
      return listView(a);
  }
}

function draw(): void {
  const me = state.kind === 'signed-in' ? state.me : undefined;
  let body;
  switch (state.kind) {
    case 'signed-out':
      body = signedOut(onSignIn, state.busy);
      break;
    case 'loading':
      body = signedOut(onSignIn, true);
      break;
    case 'signed-in':
      body = routed(app!);
      break;
    case 'no-access':
      body = noAccess(state.upn, onSignOut);
      break;
    case 'problem':
      body = problem(state.message, load);
      break;
  }
  const banner = notice
    ? html`<div class="wrap"><div class="toast ${notice.tone}" role="status">${notice.message}
        <button type="button" class="toast-x" aria-label="Dismiss" @click=${() => {
          notice = null;
          draw();
        }}>×</button></div></div>`
    : nothing;
  render([header({ me, onSignOut, onTheme: toggleTheme }), banner, body], root);
}

async function onSignIn(): Promise<void> {
  set({ kind: 'signed-out', busy: true });
  await signIn();
}

async function onSignOut(): Promise<void> {
  await signOut();
}

async function load(): Promise<void> {
  const acct = account();
  if (!acct) {
    set({ kind: 'signed-out', busy: false });
    return;
  }
  set({ kind: 'loading' });
  try {
    set({ kind: 'signed-in', me: await getMe() });
  } catch (e) {
    if (e instanceof ApiError && e.status === 403) {
      set({ kind: 'no-access', upn: acct.username });
    } else if (e instanceof ApiError && e.status === 401) {
      set({ kind: 'problem', message: `The API rejected the sign-in token (${e.message}).` });
    } else {
      set({ kind: 'problem', message: (e as Error).message || 'The API could not be reached.' });
    }
  }
}

window.addEventListener('hashchange', enterRoute);

draw();
init()
  .then(load)
  .catch((e: Error) => set({ kind: 'problem', message: e.message }));
